import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "../../hooks";
import { ChannelToggle } from "../shared/ui";
import { isValidOtpDestination } from "../../types/otpChannel";
import { VerifyPhoneIllustration } from "../shared/icons/BookingIllustrations";
const COUNTRY_CODES = [
    { code: "+91", country: "India", flag: "\u{1F1EE}\u{1F1F3}" },
    { code: "+1", country: "USA/Canada", flag: "\u{1F1FA}\u{1F1F8}" },
    { code: "+44", country: "UK", flag: "\u{1F1EC}\u{1F1E7}" },
    { code: "+971", country: "UAE", flag: "\u{1F1E6}\u{1F1EA}" },
    { code: "+966", country: "Saudi Arabia", flag: "\u{1F1F8}\u{1F1E6}" },
    { code: "+65", country: "Singapore", flag: "\u{1F1F8}\u{1F1EC}" },
    { code: "+61", country: "Australia", flag: "\u{1F1E6}\u{1F1FA}" },
    { code: "+49", country: "Germany", flag: "\u{1F1E9}\u{1F1EA}" },
    { code: "+33", country: "France", flag: "\u{1F1EB}\u{1F1F7}" },
    { code: "+81", country: "Japan", flag: "\u{1F1EF}\u{1F1F5}" },
    { code: "+86", country: "China", flag: "\u{1F1E8}\u{1F1F3}" },
    { code: "+82", country: "South Korea", flag: "\u{1F1F0}\u{1F1F7}" },
    { code: "+39", country: "Italy", flag: "\u{1F1EE}\u{1F1F9}" },
    { code: "+34", country: "Spain", flag: "\u{1F1EA}\u{1F1F8}" },
    { code: "+31", country: "Netherlands", flag: "\u{1F1F3}\u{1F1F1}" },
    { code: "+46", country: "Sweden", flag: "\u{1F1F8}\u{1F1EA}" },
    { code: "+47", country: "Norway", flag: "\u{1F1F3}\u{1F1F4}" },
    { code: "+45", country: "Denmark", flag: "\u{1F1E9}\u{1F1F0}" },
    { code: "+41", country: "Switzerland", flag: "\u{1F1E8}\u{1F1ED}" },
    { code: "+55", country: "Brazil", flag: "\u{1F1E7}\u{1F1F7}" },
    { code: "+52", country: "Mexico", flag: "\u{1F1F2}\u{1F1FD}" },
    { code: "+60", country: "Malaysia", flag: "\u{1F1F2}\u{1F1FE}" },
    { code: "+62", country: "Indonesia", flag: "\u{1F1EE}\u{1F1E9}" },
    { code: "+63", country: "Philippines", flag: "\u{1F1F5}\u{1F1ED}" },
    { code: "+66", country: "Thailand", flag: "\u{1F1F9}\u{1F1ED}" },
    { code: "+84", country: "Vietnam", flag: "\u{1F1FB}\u{1F1F3}" },
    { code: "+880", country: "Bangladesh", flag: "\u{1F1E7}\u{1F1E9}" },
    { code: "+92", country: "Pakistan", flag: "\u{1F1F5}\u{1F1F0}" },
    { code: "+94", country: "Sri Lanka", flag: "\u{1F1F1}\u{1F1F0}" },
    { code: "+977", country: "Nepal", flag: "\u{1F1F3}\u{1F1F5}" },
    { code: "+27", country: "South Africa", flag: "\u{1F1FF}\u{1F1E6}" },
    { code: "+234", country: "Nigeria", flag: "\u{1F1F3}\u{1F1EC}" },
    { code: "+254", country: "Kenya", flag: "\u{1F1F0}\u{1F1EA}" },
    { code: "+974", country: "Qatar", flag: "\u{1F1F6}\u{1F1E6}" },
    { code: "+973", country: "Bahrain", flag: "\u{1F1E7}\u{1F1ED}" },
    { code: "+968", country: "Oman", flag: "\u{1F1F4}\u{1F1F2}" },
    { code: "+965", country: "Kuwait", flag: "\u{1F1F0}\u{1F1FC}" },
    { code: "+962", country: "Jordan", flag: "\u{1F1EF}\u{1F1F4}" },
    { code: "+64", country: "New Zealand", flag: "\u{1F1F3}\u{1F1FF}" },
    { code: "+852", country: "Hong Kong", flag: "\u{1F1ED}\u{1F1F0}" },
    { code: "+960", country: "Maldives", flag: "\u{1F1F2}\u{1F1FB}" },
];
export function PhoneStep({ state, dispatch, onSendOtp }) {
    const { t } = useTranslation("appointment");
    const { otpChannel, otpChannels, countryCode, patientPhone, patientEmail, otpSending, otpError, } = state;
    const isEmail = otpChannel === "email";
    const isDisabled = otpSending ||
        !isValidOtpDestination(otpChannel, {
            countryCode,
            phoneNumber: patientPhone,
            email: patientEmail,
        });
    const isIndia = countryCode === "+91";
    const subtitle = isEmail
        ? t("verifyEmailSubtitle", "We'll email you a 6-digit code to verify.")
        : isIndia
            ? t("verifyPhoneSubtitle")
            : t("verifyPhoneSubtitleWhatsApp");
    return (_jsx(Box, { sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            p: 3,
            minHeight: 0,
            overflowY: "auto",
        }, children: _jsxs(Box, { sx: { maxWidth: 380, width: "100%" }, children: [_jsxs(Stack, { alignItems: "center", sx: { mb: 3 }, children: [_jsx(Box, { sx: { mb: 2, lineHeight: 0, color: "primary.main" }, children: _jsx(VerifyPhoneIllustration, { width: 210 }) }), _jsx(Typography, { variant: "h6", sx: { mb: 0.5 }, children: isEmail
                                ? t("verifyEmailTitle", "Verify your email")
                                : t("verifyPhoneTitle") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: subtitle })] }), _jsx(Box, { sx: { mb: 2 }, children: _jsx(ChannelToggle, { value: otpChannel, channels: otpChannels, disabled: otpSending, onChange: (next) => dispatch({ type: "SET_OTP_CHANNEL", payload: next }), labels: {
                            whatsapp: t("channelWhatsApp", "WhatsApp"),
                            email: t("channelEmail", "Email"),
                            group: t("verifyWith", "Verify with"),
                        } }) }), _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 500, mb: 0.75 }, children: isEmail
                                ? t("emailAddressLabel", "Email address")
                                : t("phoneNumberLabel") }), isEmail ? (_jsx(TextField, { type: "email", autoComplete: "email", inputMode: "email", value: patientEmail, onChange: (e) => dispatch({ type: "SET_PATIENT_EMAIL", payload: e.target.value }), placeholder: t("emailAddressPlaceholder", "you@example.com"), size: "small", fullWidth: true })) : (_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(TextField, { select: true, value: countryCode, onChange: (e) => dispatch({ type: "SET_COUNTRY_CODE", payload: e.target.value }), sx: { width: 110, flexShrink: 0 }, size: "small", children: COUNTRY_CODES.map((c) => (_jsxs(MenuItem, { value: c.code, children: [c.flag, " ", c.code] }, c.code))) }), _jsx(TextField, { type: "tel", value: patientPhone, onChange: (e) => dispatch({
                                        type: "SET_PATIENT_PHONE",
                                        payload: e.target.value,
                                    }), placeholder: t("phoneNumberPlaceholder"), size: "small", fullWidth: true })] })), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mt: 0.75, display: "block" }, children: isEmail
                                ? t("otpCaptionEmail", "We'll send a 6-digit code to this email.")
                                : t("otpCaption") }), otpError && (_jsx(Alert, { severity: "error", sx: { mt: 1.25 }, children: otpError })), _jsx(Button, { variant: "contained", fullWidth: true, onClick: onSendOtp, disabled: isDisabled, endIcon: otpSending ?
                                _jsx(CircularProgress, { size: 15, color: "inherit" })
                                : _jsx(ArrowForwardIcon, {}), sx: { mt: 2 }, children: otpSending ? t("sending") : t("sendVerificationCode") })] })] }) }));
}
